import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { InterviewOnCard } from '@/api/people';
import { Score } from '@/components/ui/Score';
import { StatusDot } from '@/components/ui/StatusDot';
import { cn } from '@/lib/cn';
import { interviewUrl } from '@/pages/Interview/interviewUrls';
import { type } from '@/theme/typography';

import { onDateTime } from './cardDates';

interface Props {
  interview: InterviewOnCard;
  /** The signed-in user's id — the only thing that tells *your* draft from *someone else's*. */
  viewerId: number;
}

/**
 * One row of §6.5's interviews block, and **the place feedback protection #3 is either kept or lost**
 * (ADR 0006, `DESIGN.md` §7).
 *
 * §6.5 distinguishes three rows, and they are told apart by two facts already in the payload —
 * `submitted_at`, and `interviewer_id` against the session user:
 *
 * | Row | What it shows |
 * |---|---|
 * | **Your own draft** | *"○ Draft"* in `ink-500`; no score and no verdict, because a draft counts towards nothing (ADR 0007) |
 * | **Someone else's draft** | the date and *"Feedback pending"* — no score, no verdict, no preview, not even a hint that content exists |
 * | **Submitted** | verdict and average, **which may still be `null`** because this viewer owes their own feedback for this person |
 *
 * ⚠️ **The hiding is not done here and must never be.** The API sends this viewer only what they may
 * read: a blinded interview arrives as a row with `overall_recommendation` and `average_score` as
 * SQL `NULL` and no `Score` rows selected at all (`apps.interviews.services.card_interviews`), so
 * there is nothing in the props to filter out. §6.5 names the edit somebody will attempt — *"fetch
 * the full interview list once and filter in the component. Do not."* — and the reason it is fatal is
 * that a UI filter leaves the data in the response, one devtools tab away.
 *
 * ⚠️ **The interviewer is named, and this docblock said they could not be for two sprints.** It read
 * *"the interviewer's name is not rendered, because the payload does not carry it … the missing field
 * is a backend item in the Backlog"* — true from TASK-064 until ADR 0016 (2026-07-30), when
 * `InterviewOnCardOut` gained `interviewer_name` beside the id it always had. §6.5's *"interviewer,
 * date, …"* is now literally what this row shows. **"You" is kept for the viewer's own** — the
 * product's own word on this card, and more useful than reading your own name back to you — so the
 * name renders for a colleague's row and *"You"* for yours.
 *
 * ⚠️ **Two of the three rows carry a link, and the third one deliberately does not** (TASK-087,
 * 2026-07-30 — the deferral this docblock used to describe, discharged). §6.5's own two lines:
 *
 *  * a **submitted** row opens the read view (§6.7) — for everybody who can see the card, because the
 *    destination decides for itself what this reader may read: the same id answers with the full
 *    interview or with metadata alone, per ADR 0006, and the link does not have to know which;
 *  * the viewer's **own draft** carries ***Continue***, which is §6.6 and the form they still owe;
 *  * a **colleague's draft stays plain text, with no link and no control.** §6.5 gives it
 *    *"interviewer and date, nothing more"*, and the destination would repeat exactly that — the
 *    blinded state's *"hidden until you submit your own feedback"* — so a link would promise content
 *    and deliver the sentence already on the row. Not an impossibility: a decision, and the row that
 *    says *"Feedback pending"* is asserted to contain no link.
 *
 * `personCard.test.tsx` walks every `href` the card renders against `buildRoutes()` — a guard written
 * in Sprint 9 against a card with no interview link at all, and non-vacuous from today.
 */
export function InterviewRow({ interview, viewerId }: Props) {
  const { t, i18n } = useTranslation('candidates');

  const isDraft = interview.submitted_at === null;
  const isYours = interview.interviewer_id === viewerId;

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2">
      {/*
       * Who conducted it: *"You"* for the viewer's own, the name for a colleague's (ADR 0016). A
       * colleague's **draft** is still a row with nothing else on it — naming who owes the write-up
       * discloses no opinion, which is the line that ADR draws.
       */}
      <span className={cn(type.bodyPrimary, isYours && 'font-medium')}>
        {isYours ? t('card.interviews.you') : interview.interviewer_name}
      </span>

      {/*
       * ⚠️ **The date says *which* date it is, and it did not until 2026-09-28** (BUG-093). This row
       * renders `scheduled_at` — when the conversation is held — while §6.7's *Other interviews* renders
       * `submitted_at` for the same interview, and both were bare: one interview showed *Jul 28, 11:30
       * AM* here and *Jul 30, 11:30 PM* there, on adjacent screens, with nothing to tell a reader that
       * two different facts were being stated. Both screens were right and the pair was unreadable.
       *
       * **This surface keeps the conversation's date** — a card is read as the record of what happened,
       * and *when we spoke* is the fact that orders these rows, including a row for a conversation that
       * has not happened yet. The label is the blinded screen's own word for the same field
       * (`blinded.scheduled`), so the product says *Scheduled* about `scheduled_at` everywhere it says
       * anything at all, and the word is interpolated into the `<time>` there for the same reason it is
       * here: the numeral role is what the whole line takes, exactly as `Interview/index.tsx` sets it.
       */}
      <time dateTime={interview.scheduled_at} className={cn(type.numeral, 'text-sm text-ink-700')}>
        {t('card.interviews.scheduledOn', {
          date: onDateTime(interview.scheduled_at, i18n.language),
        })}
      </time>

      {isDraft ? (
        /*
         * §6.5's two draft rows. Both are `ink-500` and both stop at the fact that the write-up is
         * outstanding: *that* a conversation is unanswered is §6.2's hero metric and is meant to be
         * visible, while its contents are not this reader's yet — and for the viewer's own draft
         * there is nothing to show either, because a draft counts towards nothing (ADR 0007).
         *
         * The ○ is `aria-hidden` decoration: the word beside it carries the meaning (§3 — colour and
         * shape never carry meaning alone).
         */
        <span className="flex items-center gap-1.5 text-sm text-ink-500">
          {isYours && <span aria-hidden>○</span>}
          {isYours ? t('card.interviews.draft') : t('card.interviews.pending')}
        </span>
      ) : null}

      {isDraft && isYours && (
        /*
         * ***Continue*** — §6.5's own word for the viewer's own unfinished write-up, and the one control
         * on this card that goes to §6.6. It is a real `<a>` so it opens in a new tab from here, and its
         * accessible name carries the date, because a card with three rows would otherwise announce
         * three identical *"Continue"* links (§9).
         */
        <Link
          to={interviewUrl(interview.id)}
          aria-label={t('card.interviews.continueOn', {
            date: onDateTime(interview.scheduled_at, i18n.language),
          })}
          className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {t('card.interviews.continue')}
        </Link>
      )}

      {!isDraft ? (
        <span className="ml-auto flex items-center gap-6">
          {/*
           * The read view (§6.7). Rendered for every reader of a submitted row and not only for its
           * author: the address answers per caller (ADR 0006), so a reader who still owes their own
           * feedback for this person lands on the blinded state — which is the honest answer and is
           * the same sentence this row already carries in its `—`.
           */}
          <Link
            to={interviewUrl(interview.id)}
            aria-label={t('card.interviews.readOn', {
              date: onDateTime(interview.scheduled_at, i18n.language),
            })}
            className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t('card.interviews.read')}
          </Link>
          {/*
           * `null` here is not an error and not a zero: this interview is submitted, and the viewer
           * owes feedback of their own for this person, so its contents are withheld until they
           * submit (ADR 0006). The summary row's note says which of §6.5's cases applies.
           */}
          {interview.overall_recommendation ? (
            <StatusDot value={interview.overall_recommendation} />
          ) : (
            <span className="text-sm text-ink-500" role="img" aria-label={t('latest.none')}>
              —
            </span>
          )}
          <Score value={interview.average_score} />
        </span>
      ) : null}
    </li>
  );
}
